import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import ResqlyCard from '@/components/resqly/ResqlyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const QUALIFICATION_PERIOD_OPTIONS = [
  { value: '0', label: 'No limit' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
];

const RELEASE_DELAY_OPTIONS = [
  { value: '0', label: 'Immediately' },
  { value: '1', label: '24 hours' },
  { value: '2', label: '48 hours' },
  { value: '7', label: '7 days' },
];

function ConfigField({ label, help, children }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-slate-800">{label}</label>
      {help && <p className="text-xs text-slate-500">{help}</p>}
      {children}
    </div>
  );
}

const defaultLevel = (level) => ({
  level,
  reward_type: 'percentage',
  percentage: 0,
  fixed_amount: 0,
});

export default function AdminReferrals() {
  const [overview, setOverview] = useState(null);
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [fieldHelp, setFieldHelp] = useState({});
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [password, setPassword] = useState('');
  const [adminId, setAdminId] = useState('admin');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [enableAttempt, setEnableAttempt] = useState(false);

  useEffect(() => {
    api.get('/admin/referrals/overview')
      .then(({ data }) => setOverview(data))
      .catch(() => setError('Failed to load overview'));
    api.get('/admin/referrals/config')
      .then(({ data }) => {
        setConfig(data.config);
        setDraft(JSON.parse(JSON.stringify(data.config)));
        setFieldHelp(data.field_help || {});
        setQualificationOptions(data.qualification_event_options || []);
      })
      .catch(() => setError('Failed to load configuration'));
  }, []);

  const rewardsActive = draft?.rewards?.financial_rewards_active === true;
  const rewardsOff = !rewardsActive;

  const setRewardField = (field, value) => {
    setDraft((d) => ({
      ...d,
      rewards: { ...d.rewards, [field]: value },
    }));
  };

  const setLevelField = (levelIndex, field, value) => {
    setDraft((d) => {
      const levels = [...(d.rewards?.levels || [])];
      const row = { ...levels[levelIndex], [field]: value };
      levels[levelIndex] = row;
      return { ...d, rewards: { ...d.rewards, levels } };
    });
  };

  const ensureLevels = () => {
    const max = Number(draft?.network?.max_levels || 3);
    const levels = [...(draft?.rewards?.levels || [])];
    while (levels.length < max) {
      levels.push(defaultLevel(levels.length + 1));
    }
    return levels.slice(0, max);
  };

  const buildRewardUpdates = () => {
    const levels = ensureLevels();
    return {
      network: { max_levels: Number(draft.network?.max_levels || 3) },
      rewards: {
        enabled: enableAttempt,
        levels,
        qualification_event: draft.rewards?.qualification_event,
        qualification_period_days: Number(draft.rewards?.qualification_period_days ?? 90),
        max_reward_per_referral: Number(draft.rewards?.max_reward_per_referral ?? 0),
        max_lifetime_reward: Number(draft.rewards?.max_lifetime_reward ?? 0),
        reward_release_delay_days: Number(draft.rewards?.reward_release_delay_days ?? 0),
      },
    };
  };

  const saveConfig = async () => {
    if (!password) {
      toast.error('Admin password required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/admin/referrals/config', {
        updates: buildRewardUpdates(),
        admin_id: adminId,
        password,
        reason: enableAttempt
          ? 'Admin attempted to enable referral rewards (deferred)'
          : 'Admin updated referral reward configuration',
      });
      setConfig(data.config);
      setDraft(JSON.parse(JSON.stringify(data.config)));
      setConfirmOpen(false);
      setEnableAttempt(false);
      setPassword('');
      if (data.rewards_activation_deferred) {
        toast.message('Configuration saved', {
          description: data.message || 'Referral rewards remain disabled for this release.',
        });
      } else {
        toast.success('Referral configuration saved');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed');
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = () => {
    if (enableAttempt) {
      setConfirmOpen(true);
      return;
    }
    saveConfig();
  };

  const onEnableRewardsClick = () => {
    setEnableAttempt(true);
    setConfirmOpen(true);
  };

  if (!draft) {
    return (
      <div className="resqly-shell p-5 text-sm text-slate-500">Loading referral settings…</div>
    );
  }

  const levels = ensureLevels();

  return (
    <div className="resqly-shell">
      <div className="resqly-frame min-h-screen">
        <MobileShell title="Referral Settings" backTo="/admin/dashboard">
          <div className="px-5 py-4 space-y-4" data-testid="admin-referral-dashboard">
            <Link to="/admin/dashboard" className="text-sm text-blue-700">← Admin dashboard</Link>

            {error && <p className="text-sm text-rose-600">{error}</p>}

            {overview && (
              <ResqlyCard className="p-4 space-y-2">
                <p className="font-medium">Referral activity overview</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Identities: {overview.totals?.referral_identities}</div>
                  <div>Direct links: {overview.totals?.direct_relationships}</div>
                  <div>Active: {overview.totals?.active_relationships}</div>
                  <div>Qualified: {overview.totals?.qualified_relationships}</div>
                </div>
              </ResqlyCard>
            )}

            <ResqlyCard className="p-4 space-y-4" elevated data-testid="admin-referral-rewards-status">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Referral Rewards</h2>
                  <p className="text-xs text-slate-500 mt-1">Reward configuration for future use</p>
                </div>
                <span
                  className={`text-xs font-bold px-3 py-1 rounded-full ${
                    rewardsOff
                      ? 'bg-slate-100 text-slate-700'
                      : 'bg-emerald-100 text-emerald-800'
                  }`}
                  data-testid="referral-rewards-status-badge"
                >
                  {rewardsOff ? 'OFF' : 'ON'}
                </span>
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                {draft.rewards?.status_message ||
                  'Referral tracking is active, but referral rewards are currently disabled.'}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-800">Status</p>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={rewardsOff} readOnly className="accent-[var(--resqly-primary)]" />
                    Disabled — no financial rewards are generated
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!rewardsOff}
                      readOnly
                      className="accent-[var(--resqly-primary)]"
                    />
                    Enabled — deferred until a future release
                  </label>
                </div>
                {rewardsOff && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onEnableRewardsClick}
                    data-testid="attempt-enable-referral-rewards"
                  >
                    Prepare to enable referral rewards…
                  </Button>
                )}
              </div>
            </ResqlyCard>

            <ResqlyCard className="p-4 space-y-4" data-testid="admin-referral-config">
              <h2 className="font-semibold text-slate-900">Reward configuration</h2>
              <p className="text-xs text-slate-500">
                These values are saved for future use. They do not create payouts or change balances while rewards are disabled.
              </p>

              <ConfigField label="Maximum referral levels" help={fieldHelp['network.max_levels']}>
                <Select
                  value={String(draft.network?.max_levels ?? 3)}
                  onValueChange={(v) => setDraft((d) => ({
                    ...d,
                    network: { ...d.network, max_levels: Number(v) },
                  }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                  </SelectContent>
                </Select>
              </ConfigField>

              {levels.map((row, idx) => (
                <div key={row.level} className="border rounded-lg p-3 space-y-2 bg-white">
                  <p className="text-sm font-medium">Level {row.level} reward</p>
                  <div className="grid grid-cols-2 gap-2">
                    <ConfigField label="Type">
                      <Select
                        value={row.reward_type || 'percentage'}
                        onValueChange={(v) => setLevelField(idx, 'reward_type', v)}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </ConfigField>
                    {row.reward_type === 'fixed' ? (
                      <ConfigField label="Fixed amount (₹)">
                        <Input
                          type="number"
                          min="0"
                          value={row.fixed_amount ?? 0}
                          onChange={(e) => setLevelField(idx, 'fixed_amount', Number(e.target.value))}
                        />
                      </ConfigField>
                    ) : (
                      <ConfigField label="Percentage (%)">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={row.percentage ?? 0}
                          onChange={(e) => setLevelField(idx, 'percentage', Number(e.target.value))}
                        />
                      </ConfigField>
                    )}
                  </div>
                </div>
              ))}

              <ConfigField label="Qualification event" help={fieldHelp['rewards.qualification_event']}>
                <Select
                  value={draft.rewards?.qualification_event || 'referred_user_first_booking'}
                  onValueChange={(v) => setRewardField('qualification_event', v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {qualificationOptions.map((ev) => (
                      <SelectItem key={ev} value={ev}>
                        {ev.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </ConfigField>

              <ConfigField label="Qualification period" help={fieldHelp['rewards.qualification_period_days']}>
                <Select
                  value={String(draft.rewards?.qualification_period_days ?? 90)}
                  onValueChange={(v) => setRewardField('qualification_period_days', Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUALIFICATION_PERIOD_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </ConfigField>

              <ConfigField label="Maximum reward per referral (optional)" help={fieldHelp['rewards.max_reward_per_referral']}>
                <Input
                  type="number"
                  min="0"
                  value={draft.rewards?.max_reward_per_referral ?? 0}
                  onChange={(e) => setRewardField('max_reward_per_referral', Number(e.target.value))}
                />
              </ConfigField>

              <ConfigField label="Maximum lifetime referral reward (optional)" help={fieldHelp['rewards.max_lifetime_reward']}>
                <Input
                  type="number"
                  min="0"
                  value={draft.rewards?.max_lifetime_reward ?? 0}
                  onChange={(e) => setRewardField('max_lifetime_reward', Number(e.target.value))}
                />
              </ConfigField>

              <ConfigField label="Reward release delay" help={fieldHelp['rewards.reward_release_delay_days']}>
                <Select
                  value={String(draft.rewards?.reward_release_delay_days ?? 0)}
                  onValueChange={(v) => setRewardField('reward_release_delay_days', Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RELEASE_DELAY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </ConfigField>

              <div className="space-y-2 pt-2 border-t">
                <ConfigField label="Admin ID">
                  <Input value={adminId} onChange={(e) => setAdminId(e.target.value)} />
                </ConfigField>
                <ConfigField label="Admin password (required to save)">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Confirm with admin password"
                  />
                </ConfigField>
                <Button
                  disabled={saving || !password}
                  onClick={onSaveClick}
                  data-testid="save-referral-config"
                >
                  Save reward configuration
                </Button>
              </div>
            </ResqlyCard>

            {confirmOpen && (
              <ResqlyCard
                className="p-4 space-y-3 border-2 border-amber-200 bg-amber-50"
                data-testid="enable-referral-rewards-confirm"
              >
                <h3 className="font-semibold text-slate-900">Enable Referral Rewards?</h3>
                <p className="text-sm text-slate-700">
                  Enabling this will allow qualifying referral activity to generate financial rewards
                  according to the configured rules.
                </p>
                <p className="text-xs text-amber-800 font-medium">
                  For this release, financial reward activation is deferred. Configuration will be saved
                  but rewards will remain disabled until a future release.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setConfirmOpen(false);
                      setEnableAttempt(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button className="flex-1" onClick={saveConfig} disabled={saving}>
                    Confirm and save
                  </Button>
                </div>
              </ResqlyCard>
            )}

            {overview?.recent_activity?.length > 0 && (
              <ResqlyCard className="p-4" data-testid="admin-referral-activity">
                <p className="font-medium mb-2">Recent referral activity</p>
                <ul className="text-xs space-y-1 text-slate-600">
                  {overview.recent_activity.slice(0, 8).map((ev) => (
                    <li key={ev.id}>{ev.event_type} · {ev.timestamp}</li>
                  ))}
                </ul>
              </ResqlyCard>
            )}
          </div>
        </MobileShell>
      </div>
    </div>
  );
}
